package com.advaita.cloud.drive.ui.store

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.advaita.cloud.drive.shared.DelimitedTextParser
import com.advaita.cloud.drive.shared.FileDto
import com.advaita.cloud.drive.shared.PreviewOutcome
import com.advaita.cloud.drive.shared.PreviewRenderer
import com.advaita.cloud.drive.ui.api.FilesApi
import com.advaita.cloud.drive.ui.api.PreviewNotRenderableException
import com.advaita.cloud.drive.ui.api.RedirectingException
import com.advaita.cloud.drive.ui.api.userFacingMessage
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

/** Where the tooltip should point, in viewport coordinates. */
data class TooltipAnchor(val left: Double, val top: Double, val height: Double)

/**
 * Hover-peek at a file's head, for the workflow picker's magnifier.
 *
 * Deliberately NOT the full [PreviewStore]. A tooltip cannot scroll and has no
 * room for the delimiter or header controls, so it shows a bounded head and
 * nothing else: [MAX_LINES] lines, [MAX_COLUMNS] columns, each cell clipped.
 * That is the whole trade — it is quick to scan several candidates, and it is
 * not the tool for reading a wide TSV in full.
 *
 * Fetches are DELAYED and CACHED. Without the delay, sweeping the pointer down
 * a twenty-row listing fires twenty requests; without the cache, going back to
 * a row refetches it.
 */
class PreviewTooltipStore {

    /** The file being hovered, or null when nothing is. */
    var target: FileDto? by mutableStateOf(null)
        private set

    var anchor: TooltipAnchor? by mutableStateOf(null)
        private set

    /** Rendered head, already bounded. Null while loading. */
    var body: String? by mutableStateOf(null)
        private set

    /** Shown instead of [body] when there is nothing to draw, or a fetch failed. */
    var note: String? by mutableStateOf(null)
        private set

    private val cache = mutableMapOf<String, Pair<String?, String?>>()
    private var job: Job? = null

    /** Begin a hover. Cheap to call repeatedly for the same file. */
    fun show(file: FileDto, at: TooltipAnchor) {
        if (target?.id == file.id) {
            anchor = at
            return
        }
        job?.cancel()
        target = file
        anchor = at
        val cached = cache[file.id]
        if (cached != null) {
            body = cached.first
            note = cached.second
            return
        }
        body = null
        note = null
        job = MainScope().launch { load(file) }
    }

    /** End the hover. Abandons any in-flight fetch. */
    fun hide() {
        job?.cancel()
        job = null
        target = null
        anchor = null
        body = null
        note = null
    }

    private suspend fun load(file: FileDto) {
        try {
            // Hover intent: only spend a request once the pointer has settled.
            delay(HOVER_DELAY_MS)
            val descriptor = FilesApi.previewDescriptor(file.id)
            if (target?.id != file.id) return

            if (descriptor.outcome != PreviewOutcome.RENDER) {
                remember(
                    file.id,
                    null,
                    descriptor.message ?: "No preview for this file type.",
                    // NOT_READY is the one outcome that changes on its own: the
                    // upload finishes and the same file becomes renderable.
                    // Cached, it pins "not ready" until a page reload. Every
                    // other outcome follows from the name or the stored size,
                    // so it stays cached — otherwise a hover over any
                    // unsupported file in a folder refetches its descriptor.
                    cacheable = descriptor.outcome != PreviewOutcome.NOT_READY,
                )
                return
            }
            // The renderer is checked BEFORE the bytes are asked for. A peek can
            // only draw text or a grid, and asking first made a hover over an
            // image produce a full server-side rendition that this tooltip then
            // threw away. The outcome follows the file type, so it is cacheable.
            //
            // A DERIVED payload is refused for that same reason, and the
            // renderer alone cannot see it: a workbook is TABULAR, so it passed
            // this check and made a 250 ms hover read and parse the whole file.
            val drawable = !descriptor.derived &&
                (descriptor.renderer == PreviewRenderer.TABULAR ||
                    descriptor.renderer == PreviewRenderer.TEXT)
            if (!drawable) {
                // NOT "no preview for this file type". The outcome is RENDER, so
                // the pane draws this file — an image peek is the thing this
                // tooltip cannot do, and saying otherwise sends the reader away
                // from a preview that is one click off.
                remember(file.id, null, "Open this file to see the preview.")
                return
            }

            val fetched = FilesApi.previewContent(file.id)
            if (target?.id != file.id) return
            val text = fetched.response.text().await().removePrefix("﻿")
            if (target?.id != file.id) return

            val rendered = when (descriptor.renderer) {
                PreviewRenderer.TABULAR -> tabularHead(text, file.displayName, fetched.truncated)
                else -> textHead(text)
            }
            remember(file.id, rendered, null)
        } catch (e: CancellationException) {
            throw e
        } catch (e: PreviewNotRenderableException) {
            // Same as the pane: a 409 is policy answering, not a failure. The
            // outcome is a property of the file, so it is cacheable.
            remember(file.id, null, e.descriptor.message ?: "No preview for this file.")
        } catch (e: RedirectingException) {
            throw e
        } catch (e: Throwable) {
            if (target?.id == file.id) note = e.userFacingMessage()
        }
    }

    private fun remember(fileId: String, rendered: String?, message: String?, cacheable: Boolean = true) {
        if (cacheable) cache[fileId] = rendered to message
        if (target?.id == fileId) {
            body = rendered
            note = message
        }
    }

    /** Column-aligned head. Padding is what makes a grid readable without a table. */
    private fun tabularHead(text: String, fileName: String, truncated: Boolean): String {
        val delimiter = DelimitedTextParser.detectDelimiter(text, fileName)
        val table = DelimitedTextParser.parse(
            text = text,
            delimiter = delimiter,
            firstRowIsHeader = true,
            dropPartialLastRecord = truncated,
            maxRows = MAX_LINES,
            maxColumns = MAX_COLUMNS,
        )
        val rows = buildList {
            table.header?.let { add(it) }
            addAll(table.rows)
        }
        if (rows.isEmpty()) return ""
        val widths = IntArray(table.columnCount) { col ->
            rows.maxOf { it.getOrNull(col)?.length ?: 0 }.coerceAtMost(MAX_CELL_CHARS)
        }
        return rows.joinToString("\n") { row ->
            row.indices.joinToString("  ") { col ->
                clip(row[col], widths[col]).padEnd(widths[col])
            }.trimEnd()
        }
    }

    private fun textHead(text: String): String =
        text.lineSequence().take(MAX_LINES).joinToString("\n") { clip(it, MAX_LINE_CHARS) }

    private fun clip(value: String, max: Int): String =
        if (value.length <= max) value else value.take(max - 1) + "…"

    private companion object {
        const val HOVER_DELAY_MS = 250L
        const val MAX_LINES = 12
        const val MAX_COLUMNS = 6
        const val MAX_CELL_CHARS = 18
        const val MAX_LINE_CHARS = 72
    }
}
